import React, { useState } from "react";
import Button from "../../Button";
import styles from "./ActionButtons.module.css";
import RejectionReasonModal from "../RejectionReasonModal";
import ApprovalConfirmationModal from "../ApprovalConfirmationModal";
import CertificateUploadModal from "../CertificateUploadModal";
import { APPROVAL_TYPE_CODES } from "../../../../constants/approvalTypes";

const ActionButtons = ({ 
  requestProgress, 
  children, 
  handleAction, 
  requestProgressId, 
  prevComments,
  isRA = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  if (!(requestProgress?.status?.includes("active") && children?.length)) {
    return null;
  }

  const handleButtonClick = async (child) => {
    const isRejection = child.ApprovalType?.code === APPROVAL_TYPE_CODES.REJECTED;
    const isApproval = child.ApprovalType?.code === APPROVAL_TYPE_CODES.APPROVED;
    
    if (isRejection) {
      setSelectedAction(child);
      setIsModalOpen(true);
    } else if (isApproval) {
      setSelectedAction(child);
      if (isRA) {
        setIsCertificateModalOpen(true);
      } else {
        setIsApprovalModalOpen(true);
      }
    } else {
      setIsProcessing(true);
      try {
        console.log("[ActionButtons] Calling handleAction for:", child.ApprovalType?.name);
        await handleAction({ child, requestProgressId, prevComments });
        console.log("[ActionButtons] handleAction completed successfully");
      } catch (error) {
        console.error("[ActionButtons] Error processing action:", error);
      } finally {
        setIsProcessing(false);
        console.log("[ActionButtons] Processing complete, isProcessing set to false");
      }
    }
  };

  const handleModalConfirm = async (rejectionReason) => {
    setIsProcessing(true);
    try {
      // Call handleAction with rejection reason
      await handleAction({ 
        child: selectedAction, 
        requestProgressId, 
        prevComments,
        rejectionReason 
      });
      setIsModalOpen(false);
      setSelectedAction(null);
    } catch (error) {
      console.error("Error processing rejection:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApprovalConfirm = async () => {
    setIsProcessing(true);
    try {
      // Call handleAction for regular approval
      await handleAction({ 
        child: selectedAction, 
        requestProgressId, 
        prevComments 
      });
      setIsApprovalModalOpen(false);
      setSelectedAction(null);
    } catch (error) {
      console.error("Error processing approval:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCertificateConfirm = async (certificateFileId, certificateLicenseNumber) => {
    setIsProcessing(true);
    try {
      // Call handleAction for RA approval with certificate and license number
      await handleAction({ 
        child: selectedAction, 
        requestProgressId, 
        prevComments,
        certificateFileId,
        certificateLicenseNumber
      });
      setIsCertificateModalOpen(false);
      setSelectedAction(null);
    } catch (error) {
      console.error("Error processing approval with certificate:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleModalClose = () => {
    if (!isProcessing) {
      setIsModalOpen(false);
      setSelectedAction(null);
    }
  };

  const handleApprovalModalClose = () => {
    if (!isProcessing) {
      setIsApprovalModalOpen(false);
      setSelectedAction(null);
    }
  };

  const handleCertificateModalClose = () => {
    if (!isProcessing) {
      setIsCertificateModalOpen(false);
      setSelectedAction(null);
    }
  };

  return (
    <>
      <div className={styles.actionButtonsContainer}>
        {children.map((child) => (
          <Button
            key={child.id}
            color="primary"
            className={`${styles.actionButtonStyle} ${child.ApprovalType?.code === APPROVAL_TYPE_CODES.COMMITTEE ? styles.sendToCommittee : ""}`}
            onClick={() => handleButtonClick(child)}
            text={child?.ApprovalType?.name || ""}
            disabled={isProcessing}
          />
        ))}
      </div>
      
      <RejectionReasonModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
        isProcessing={isProcessing}
      />
      
      <ApprovalConfirmationModal
        isOpen={isApprovalModalOpen}
        onClose={handleApprovalModalClose}
        onConfirm={handleApprovalConfirm}
        isProcessing={isProcessing}
      />
      
      <CertificateUploadModal
        isOpen={isCertificateModalOpen}
        onClose={handleCertificateModalClose}
        onConfirm={handleCertificateConfirm}
        isProcessing={isProcessing}
        requestName={requestProgress?.request?.study_name || ""}
      />
    </>
  );
};

export default ActionButtons; 